import { format, formatISO, isValid, parseISO } from "date-fns";

/**
 * Convert a datetime from ISO string representation to local representation.
 * If the provided dateString is invalid, the same string is returned.
 * @param dateString The iso string date to be converted.
 * @returns The same date formatted as dd/MM/yyyy HH:mm:ss.
 */
export function toDateTimeString(dateString: string): string {
	return formatDateString(dateString, "dd/MM/yyyy HH:mm:ss");
}

/**
 * Convert a date from ISO string representation to local representation.
 * If the provided dateString is invalid, the same string is returned.
 * @param dateString The iso string date to be converted.
 * @returns The same date formatted as dd/MM/yyyy.
 */
export function toDateString(dateString: string): string {
	return formatDateString(dateString, "dd/MM/yyyy");
}

function formatDateString(dateString: string, formatString: string): string {
	const date = new Date(dateString);
	if (isValid(date)) {
		return format(date, formatString);
	} else {
		return dateString;
	}
}


export function stringToDate(value: string): Date | null {
	const date = parseISO(value);
	if (isValid(date)) {
		return date;
	} else {
		return null;
	}
}

export function dateToString(date: Date | null): string {
	if (!date || !isValid(date)) {
		return "";
	}

	try {
		return formatISO(date, { representation: "date" });
	} catch (error) {
		return date.toString() ?? "";
	}
}

/**
 * Is the given date before a certain other date relative to today.
 * The days offset can be both positive as negative.
 * @param dateString The iso string date.
 * @param days The amount of days from now to compare it to.
 * @returns True if the date is before today + days.
 **/
export function isBefore(dateString: string, days: number): boolean {
	const targetDate = new Date();
	targetDate.setDate(targetDate.getDate() + days);
	const date = new Date(dateString);
	return date < targetDate;
}

/**
 * Is the given date after a certain other date relative to today.
 * The days offset can be both positive as negative.
 * @param dateString The iso string date.
 * @param days The amount of days from now to compare it to.
 * @returns True if the date is after today + days.
 **/
export function isAfter(dateString: string, days: number): boolean {
	const targetDate = new Date();
	targetDate.setDate(targetDate.getDate() + days);
	const date = new Date(dateString);
	return date > targetDate;
}

/**
 *
 * Sort function for two date strings.
 *
 * @param a The first date string.
 * @param b The second date string.
 * @returns A negative number if a is before b,
 * 		a positive number if b is before a and 0 if both are the same.
 **/
export function sort(a: string | null, b: string | null): number {
	if (a !== null && b !== null) {
		const aDate = new Date(a);
		const bDate = new Date(b);
		return aDate.getTime() - bDate.getTime();
	} else if (a === null && b !== null) {
		return -1;
	} else if (a !== null && b === null) {
		return 1;
	} else {
		return 0;
	}
}
